<template>
  <div class="action-config-item">
    <div class="mb-4">
      <label for="action-name" class="block text-sm font-semibold text-gray-700">Action Name</label>
      <el-input
        v-model="localStep.label"
        id="action-name"
        placeholder="Enter Action Name"
        clearable
        class="w-full rounded-md"
      />
    </div>
    <!-- Field Selector -->
    <div class="field-select-container mb-3">
      <label class="field-label">Field to Update</label>
      <el-select
        v-model="localStep.update_field"
        placeholder="Select field to update"
        @change="setFieldDetails()"
        clearable
        filterable
        class="full-width"
      >
        <el-option
          v-for="(field, i) in getFieldsByType([], allFields)"
          :key="field.key + i"
          :value="field.key"
          :label="field.label"
        />
      </el-select>
    </div>

    <!-- Field Configuration -->
    <div v-if="showFieldConfiguration && currentField" class="field-config-container mb-3">
      <label class="field-label">Field Configuration</label>

      <!-- Field Execution Component -->
      <AnyFieldsExecute
        v-if="shouldShowFieldExecute"
        :fieldsData="allFields"
        :field="currentField"
        :form="localStep"
        class="field-execute"
      />

      <!-- Operator Selector for AUTO_INCREMENT_NUMBER -->
      <el-select
        v-else-if="currentField.input_type === 'AUTO_INCREMENT_NUMBER'"
        v-model="localStep.operator"
        placeholder="Select operator"
        clearable
        filterable
        class="full-width mt-2"
      >
        <el-option value="GENERATE_NEW_NUMBER" label="Generate new number" />
      </el-select>

      <!-- Make Empty Checkbox -->
      <div class="mt-2">
        <el-checkbox
          v-model="localStep.make_empty"
          :disabled="localStep[localStep.update_field] ? true : false"
        >
          Make empty
        </el-checkbox>
      </div>
    </div>

    <!-- Field Mapping Section -->
    <div v-if="showMappingOptions" class="mapping-container flex-col gap-2 mt-2">
      <span v-if="!localStep.update_field_mapped_with" class="or-text">OR</span>

      <label class="field-label">Map with field</label>
      <el-select
        v-model="localStep.update_field_mapped_with"
        placeholder="Map with field"
        clearable
        filterable
        :disabled="localStep[localStep.update_field] ? true : false"
        class="full-width"
      >
        <el-option
          v-for="(field, i) in mappingFields"
          :key="field.key + i"
          :value="field.key"
          :label="field.label"
        />
        <el-option
          v-if="showCurrentTimeOption"
          value="CURRENT_TIME"
          label="Map with current time"
        />
      </el-select>
    </div>

    <!-- Footer Actions -->
    <div class="automation-footer-actions mt-4 flex justify-end gap-2">
      <el-button @click="cancelChanges">Cancel</el-button>
      <el-button type="primary" @click="saveChanges">Save</el-button>
    </div>
  </div>
</template>

<script>
  import StepEditableMixin from './StepEditableMixin';

  export default {
    name: 'SelfUpdateAction',
    props: {
      allFields: Array,
      step: Object,
    },
    mixins: [StepEditableMixin],
    data() {
      return {
        stepRefresh: false,
      };
    },
    components: {
      AnyFieldsExecute: () =>
        import('@/components/templates/formComponentsExecute/AnyFieldsExecute.vue'),
    },
    computed: {
      compatibleFields() {
        return this.getFieldsByType([], this.allFields);
      },
      showFieldConfiguration() {
        return this.localStep.update_field && !this.stepRefresh;
      },
      currentField() {
        return this.getFieldProperty(this.allFields, this.localStep.update_field, 'key', '', {});
      },
      shouldShowFieldExecute() {
        return (
          !this.localStep.make_empty &&
          this.currentField &&
          this.currentField.input_type !== 'AUTO_INCREMENT_NUMBER'
        );
      },
      showMappingOptions() {
        return !this.localStep.make_empty && this.localStep.update_field;
      },
      mappingFields() {
        const currentFieldType = this.getFieldProperty(
          this.allFields,
          this.localStep.update_field,
          'key',
          'input_type',
          ''
        );
        return this.getFieldsByType([currentFieldType], this.allFields).filter(
          (e) => e.key !== this.localStep.update_field
        );
      },
      showCurrentTimeOption() {
        const fieldType = this.getFieldProperty(
          this.allFields,
          this.localStep.update_field,
          'key',
          'input_type',
          ''
        );
        return ['DATE', 'DATE_TIME'].includes(fieldType);
      },
    },
    methods: {
      setFieldDetails() {
        if (this.localStep.update_field_mapped_with) {
          delete this.localStep.update_field_mapped_with;
        }
        this.stepRefresh = true;
        setTimeout(() => {
          this.stepRefresh = false;
        }, 100);
      },
    },
  };
</script>

<style scoped>
  .action-config-item {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 12px 0;
    font-family: 'Segoe UI', Roboto, sans-serif;
    height: 100%; /* Ensure full height */
    min-height: 400px; /* Set a minimum height */
  }

  .field-label {
    display: block;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 4px;
    color: #303133;
  }

  .full-width {
    width: 100%;
  }

  .field-select-container,
  .field-config-container {
    display: flex;
    flex-direction: column;
  }

  .field-config-container {
    background: #f9f9f9;
    padding: 14px;
    border-radius: 6px;
    border: 1px solid #dcdfe6;
  }

  .mapping-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .or-text {
    font-size: 12px;
    color: #909399;
    font-weight: 500;
    text-align: center;
  }

  .mt-2 {
    margin-top: 8px;
  }

  .mb-3 {
    margin-bottom: 12px;
  }

  .field-execute {
    margin-top: 6px;
  }
</style>
<style>
  .automation-footer-actions {
    margin-top: auto !important; /* pushes footer to bottom */
    border-top: 1px solid #ebeef5;
    padding-top: 12px;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
  .automation-footer-actions .el-button {
    font-weight: 500;
    border-radius: 6px;
    padding: 0 14px;
    height: 32px;
  }
  .action-config-item {
    height: 100%;
    min-height: 500px;
  }
</style>
